import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/AuthContext';
import mimo_logo from '../assets/Mimo_Logo_R.jpg';

const { Title, Text, Paragraph } = Typography;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.dev.simscope.click';

// KAN-180: public activation page. The invite email links here with the
// single-use token in the URL fragment (never sent to a server). The user
// picks a password; the backend sets it as permanent and they sign in as usual.

export function readTokenFromHash(hash: string): string | null {
  const m = /(?:^#|&)token=([0-9a-f]{64})(?:&|$)/i.exec(hash || '');
  return m ? m[1].toLowerCase() : null;
}

// Mirrors the Cognito pool policy (8+ chars, upper, lower, digit) so most
// mistakes are caught before the round trip; Cognito remains the authority.
// KAN-207: an admin-sent password reset uses the same page and token flow;
// `reset=1` in the fragment only changes the wording.
export function isResetLink(hash: string): boolean {
  return /(?:^#|&)reset=1(?:&|$)/.test(hash || '');
}

export function passwordProblems(pw: string): string[] {
  const problems: string[] = [];
  if (pw.length < 8) problems.push('at least 8 characters');
  if (!/[a-z]/.test(pw)) problems.push('a lowercase letter');
  if (!/[A-Z]/.test(pw)) problems.push('an uppercase letter');
  if (!/[0-9]/.test(pw)) problems.push('a number');
  return problems;
}

type State = 'form' | 'submitting' | 'done';

export default function Activate() {
  const auth = useAuth();
  const token = useMemo(() => readTokenFromHash(window.location.hash), []);
  const isReset = useMemo(() => isResetLink(window.location.hash), []);
  const [state, setState] = useState<State>('form');
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [form] = Form.useForm<{ password: string; confirm: string }>();

  useEffect(() => {
    document.title = `${isReset ? 'Set a new password' : 'Activate your account'} – MIMO Connect`;
  }, []);

  const submit = async ({ password }: { password: string }) => {
    if (!token) return;
    setState('submitting');
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.detail ? `${body.error}: ${body.detail}` : body.error || 'Activation failed');
        setState('form');
        return;
      }
      setEmail(body.email || null);
      setState('done');
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
      setState('form');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0a',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 460, borderRadius: 12 }}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <img src={mimo_logo} alt="MIMO Connect" style={{ maxWidth: 220 }} />
        </div>

        {!token && (
          <>
            <Title level={4}>This link is not valid</Title>
            <Paragraph type="secondary">
              The link is incomplete or has already been used. Ask your administrator to send you a
              new one from the Users page.
            </Paragraph>
          </>
        )}

        {token && state !== 'done' && (
          <>
            <Title level={4}>{isReset ? 'Set a new password' : 'Activate your account'}</Title>
            <Paragraph type="secondary">
              {isReset
                ? 'Choose a new password for the MIMO Connect Portal. You will be signed out on your other devices.'
                : 'Choose the password you will use to sign in to the MIMO Connect Portal.'}
            </Paragraph>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
            <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    validator: (_, v: string) => {
                      const p = passwordProblems(v || '');
                      return p.length
                        ? Promise.reject(new Error(`Needs ${p.join(', ')}.`))
                        : Promise.resolve();
                    },
                  },
                ]}
                extra="At least 8 characters with an uppercase letter, a lowercase letter and a number."
              >
                <Input.Password prefix={<LockOutlined />} autoComplete="new-password" autoFocus />
              </Form.Item>
              <Form.Item
                name="confirm"
                label="Confirm password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Repeat the password.' },
                  ({ getFieldValue }) => ({
                    validator: (_, v: string) =>
                      v === getFieldValue('password')
                        ? Promise.resolve()
                        : Promise.reject(new Error('The passwords do not match.')),
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={state === 'submitting'}>
                {isReset ? 'Set password' : 'Activate account'}
              </Button>
            </Form>
            <Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
              {isReset
                ? 'Reset links work once and expire after 24 hours.'
                : 'Activation links work once and expire after 7 days.'}
            </Paragraph>
          </>
        )}

        {state === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a', marginBottom: 12 }} />
            <Title level={4}>
              {isReset ? 'Your password has been changed' : 'Your account is active'}
            </Title>
            <Paragraph type="secondary">
              {email ? (
                <>
                  On the sign-in page enter <Text strong>{email}</Text> in the{' '}
                  <Text strong>Username</Text> field, then the password you just chose.
                </>
              ) : (
                'On the sign-in page enter your email address in the Username field, then the password you just chose.'
              )}
            </Paragraph>
            <Button type="primary" block onClick={() => auth.login()}>
              Sign in
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
